import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { GridModule } from '@progress/kendo-angular-grid';
import {
  NotificationModule,
  NotificationService,
} from '@progress/kendo-angular-notification';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import 'hammerjs';
import { OnboardingDashboardComponent } from 'src/app/pages/onboarding/dashboards/dashboard/dashboard.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ErrorResponse } from './core/abstracts/models/error-response.abstract';
import { CoreModule } from './core/core.module';
import { HeadersInterceptor } from './core/interceptors/headers.interceptor';
import { ServersideAlertInterceptor } from './core/interceptors/serverside-alert.interceptor';
import { PageStateResolver } from './core/resolvers/onboarding/navigation/page-state.resolver';
import { LoggerService } from './core/services/common/logger.service';
import { RequestObserverService } from './core/services/common/request-observer.service';
import { HydrationService } from './core/services/login/hydration.service';
import { PageStateService } from './core/services/onboarding/page-state.service';
import { metaReducers, reducers } from './core/store/common/reducers';
import { LoginModule } from './pages/login/login.module';
import { ClientAdditionalInfoRequestDashboardComponent } from './pages/onboarding/dashboards/client-additional-info-request-dashboard/client-additional-info-request-dashboard.component';
import { DisqualifiedDashboardComponent } from './pages/onboarding/dashboards/disqualified-dashboard/disqualified-dashboard.component';
import { MsaDashboardComponent } from './pages/onboarding/dashboards/msa-dashboard/msa-dashboard.component';
import { NowaccountActivateDashboardComponent } from './pages/onboarding/dashboards/nowaccount-activate-dashboard/nowaccount-activate-dashboard.component';
import { DocumentsComponent } from './pages/onboarding/documents/documents.component';
import { LegacyUpgradeNavigatorComponent } from './pages/onboarding/legacy-upgrade-navigator/legacy-upgrade-navigator.component';
import { LegacyUpgradeComponent } from './pages/onboarding/legacy-upgrade/legacy-upgrade.component';
import { PreDashboardComponent } from './pages/onboarding/pre-dashboard/pre-dashboard.component';
import { RedirectComponent } from './pages/onboarding/redirect/redirect.component';
import { CommonSharedModule } from './shared/common/common-shared.module';
import { EmailMaskPipe } from './shared/common/email-mask/email-mask.pipe';
import { KendoModule } from './shared/kendo/kendo.module';
import { OnboardingSharedModule } from './shared/onboarding/onboarding-shared.module';

export function loadPageStates(ps: PageStateService, logger: LoggerService) {
  return async () => {
    try {
      await ps.loadPageStates();
    } catch (exception) {
      if (exception instanceof ErrorResponse) logger.error(exception.message);
    }
  };
}

export function loadAppStoreViaLocalStorage(
  hs: HydrationService,
  logger: LoggerService,
) {
  return () => {
    try {
      console.log('app.module loadAppStoreViaLocalStorage');
      hs.loadAppStoreViaLocalStorage();
    } catch (exception) {
      if (exception instanceof ErrorResponse) logger.error(exception.message);
    }
  };
}

@NgModule({
  declarations: [
    AppComponent,
    OnboardingDashboardComponent,
    DocumentsComponent,
    EmailMaskPipe,
    DisqualifiedDashboardComponent,
    PreDashboardComponent,
    LegacyUpgradeComponent,
    LegacyUpgradeNavigatorComponent,
    NowaccountActivateDashboardComponent,
    MsaDashboardComponent,
    RedirectComponent,
    ClientAdditionalInfoRequestDashboardComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    ChartsModule,
    GridModule,
    HttpClientModule,
    CoreModule.forRoot(),
    CommonModule,
    NgxDatatableModule,
    CommonSharedModule,
    OnboardingSharedModule,
    KendoModule,
    LoginModule,
    GridModule,
    NotificationModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() }),
    DialogsModule,
  ],
  providers: [
    PageStateService,
    PageStateResolver,
    {
      provide: APP_INITIALIZER,
      useFactory: loadPageStates,
      deps: [PageStateService, LoggerService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: loadAppStoreViaLocalStorage,
      deps: [HydrationService, LoggerService],
      multi: true,
    },
    LoggerService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeadersInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServersideAlertInterceptor,
      multi: true,
    },
    NotificationService,
    RequestObserverService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
